import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProducts, getItemsPerWord } from "../../../server/Product";
import { getBySlug } from "../../../server/Category";
import { setBaseProducts, reload } from "../../../server/ProductsFilters";
import { extractProductsFromTree } from "./useCategoryUtils";
import { use } from "react";

export const useProductsFilters = (stationId) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.productFilters.filters);
  const baseProducts = useSelector(
    (state) => state.productFilters.baseProducts,
  );
  const categoryChips = useSelector(
    (state) => state.productFilters.filters.categoryChips,
  );

  useEffect(() => {
    const run = async () => {
      const hasNonCategoryFilters =
        filters.brandIds.length > 0 ||
        filters.subBrandIds.length > 0 ||
        filters.gtin;

      const hasCategoryFilters = categoryChips.length > 0;

      if (!hasNonCategoryFilters && !hasCategoryFilters) {
        dispatch(setBaseProducts([]));
        return;
      }

      dispatch(reload());

      let result = null;

      if (hasNonCategoryFilters) {
        const groups = [];

        if (filters.brandIds.length > 0) {
          const results = await Promise.all(
            filters.brandIds.map((id) =>
              getProducts(stationId, { brandId: id }).catch(() => []),
            ),
          );
          groups.push(
            Object.values(
              results.flat().reduce((acc, p) => {
                acc[String(p.id || p.productId)] = p;
                return acc;
              }, {}),
            ),
          );
        }

        if (filters.subBrandIds.length > 0) {
          const results = await Promise.all(
            filters.subBrandIds.map((id) =>
              getProducts(stationId, { subBrandId: id }).catch(() => []),
            ),
          );
          groups.push(
            Object.values(
              results.flat().reduce((acc, p) => {
                acc[String(p.id || p.productId)] = p;
                return acc;
              }, {}),
            ),
          );
        }

        if (filters.gtin) {
          const gtinRes = await getProducts(stationId, {
            gtin: filters.gtin.replace(/^0+/, ""),
          }).catch(() => []);
          if (!gtinRes?.length) {
            dispatch(setBaseProducts([]));
            return;
          }
          groups.push(gtinRes);
        }

        result = groups.reduce((acc, group) => {
          const ids = new Set(group.map((p) => String(p.id || p.productId)));
          return acc.filter((p) => ids.has(String(p.id || p.productId)));
        });
      }

      if (hasCategoryFilters) {
        const activeIds = categoryChips.map((c) => c.slug);

        if (result !== null) {
          result = result.filter(
            (p) =>
              (p.categories?.length ?? 0) > 0 &&
              activeIds.some((slug) =>
                p.categories?.[0]?.categoryHierarchy?.find(
                  (cat) => cat.slug === slug,
                ),
              ),
          );
        } else {
          const results = await Promise.all(
            categoryChips.map(({ slug }) => {
              if (!slug) return Promise.resolve([]);
              return getBySlug(stationId, slug, false, true)
                .then(extractProductsFromTree)
                .catch(() => []);
            }),
          );
          result = Object.values(
            results.flat().reduce((acc, p) => {
              acc[String(p.id || p.productId)] = p;
              return acc;
            }, {}),
          );
        }
      }

      dispatch(setBaseProducts(result));
    };

    run();
  }, [dispatch, stationId, filters, categoryChips]);


  useEffect(() => {
    if (baseProducts?.length === 0 && filters.search.trim().length > 1)
      dispatch(getItemsPerWord(filters.search, stationId, "gtin,name,variant,status,brand,sub_brand,user_company,description,kosher_supervision,weight_details,weight_jump", false),);
    if (filters.search.trim() === "")
      dispatch(setBaseProducts(baseProducts));
  }, [filters.search]);

  return baseProducts;
};
